'use client'

import { isArray, isNil } from 'lodash-es'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  TSearchFilter,
  useSearchFilterStore,
  defaultFilters,
  RoomDiscount,
  PopularOption,
  Floor,
  PyeongSize,
  RoomCount,
} from '@/entities/map'
import { PropertyType, BasicOption, AdditionalOption } from '@/entities/room'

export enum FilterType {
  Dialog = 'dialog',
  Default = 'default',
}

export enum Filter {
  RoomCounts = 'roomCounts',
  UsingFee = 'usingFee',
  PropertyTypes = 'propertyTypes',
  PyeongSizes = 'pyeongSizes',
  Floors = 'floors',
  Options = 'options',
  RoomDiscounts = 'roomDiscounts',
}

type FilterValue =
  | RoomCount
  | PropertyType
  | PyeongSize
  | Floor
  | PopularOption
  | BasicOption
  | AdditionalOption
  | RoomDiscount
  | number
  | null

type FilterState = {
  type: FilterType
  filter: TSearchFilter
  updateFilter: (newFilters: {
    [key in keyof TSearchFilter]?: FilterValue
  }) => void
  resetFilter: () => void
}

const FilterContext = createContext<FilterState | null>(null)

type ProviderProps = PropsWithChildren

const getUpdatedFilter = (
  prevFilter: TSearchFilter,
  newFilters: { [key in keyof TSearchFilter]?: FilterValue },
) => {
  const updatedFilter = Object.entries(newFilters).map(([key, value]) => {
    const prevValue = prevFilter[key as keyof TSearchFilter]

    if (!isArray(prevValue)) {
      return [key, value]
    }

    if (!isNil(value) && prevValue.includes(value as never)) {
      return [key, prevValue.filter(item => item !== value)]
    }

    return [key, [...prevValue, value]]
  })

  return {
    ...prevFilter,
    ...(Object.fromEntries(updatedFilter) as Partial<TSearchFilter>),
  }
}

export const FilterProvider = ({ children }: ProviderProps) => {
  const filterState = useSearchFilterStore(state => state.filters)

  const [filter, setFilter] = useState<TSearchFilter>(filterState)

  const updateFilter = useCallback(
    (newFilters: { [key in keyof TSearchFilter]?: FilterValue }) => {
      setFilter(prevFilter => getUpdatedFilter(prevFilter, newFilters))
    },
    [setFilter],
  )

  const resetFilter = useCallback(() => {
    setFilter(prevFilter => {
      return {
        ...defaultFilters,
        startDate: prevFilter.startDate,
        endDate: prevFilter.endDate,
      }
    })
  }, [setFilter])

  const value = useMemo(
    () => ({ type: FilterType.Dialog, filter, updateFilter, resetFilter }),
    [filter, updateFilter, resetFilter],
  )

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  )
}

export const FilterStoreProvider = ({ children }: PropsWithChildren) => {
  const filterState = useSearchFilterStore(state => state.filters)
  const actions = useSearchFilterStore(state => state.actions)

  const updateFilter = useCallback(
    (newFilters: { [key in keyof TSearchFilter]?: FilterValue }) => {
      const newFilter = getUpdatedFilter(filterState, newFilters)
      actions.updateFilter(newFilter)
    },
    [actions, filterState],
  )

  const resetFilter = useCallback(() => {
    actions.resetFilters()
  }, [actions])

  const value = useMemo(
    () => ({
      type: FilterType.Default,
      filter: filterState,
      updateFilter,
      resetFilter,
    }),
    [filterState, updateFilter, resetFilter],
  )

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  )
}

export const useFilter = () => {
  const context = useContext(FilterContext)

  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider')
  }

  return context
}
